import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Calendar, Clock, User, Phone, Mail, MapPin, CheckCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";

const quoteToBookingSchema = z.object({
  scheduled_date: z.string().min(1, "Date is required"),
  scheduled_time: z.string().min(1, "Time is required"),
  customer_name: z.string().min(1, "Customer name is required"),
  customer_phone: z.string().min(1, "Customer phone is required"),
  customer_email: z.string().email("Valid email is required"),
  service_address: z.string().min(10, "Service address is required"),
});

type QuoteToBookingForm = z.infer<typeof quoteToBookingSchema>;

interface QuoteToBookingConverterProps {
  quoteRequest: {
    id: string;
    customer_id: string;
    company_id: string;
    service_id: string;
    quote_price: number;
    special_requests?: string;
    photos?: string[];
    status: string;
    /** Date proposed by the customer when requesting the quote (optional). */
    scheduled_date?: string;
    /** Time proposed by the customer when requesting the quote (optional). */
    scheduled_time?: string;
    /** Contact details captured at quote request time (optional). */
    customer_name?: string;
    customer_email?: string;
    customer_phone?: string;
  };
  onBookingCreated: (bookingId: string) => void;
  onClose: () => void;
}

export default function QuoteToBookingConverter({ 
  quoteRequest, 
  onBookingCreated, 
  onClose 
}: QuoteToBookingConverterProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceAddresses, setServiceAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useI18n();

  const form = useForm<QuoteToBookingForm>({
    resolver: zodResolver(quoteToBookingSchema),
    defaultValues: {
      // We intentionally leave fields empty here. The useEffect below pulls
      // canonical data from the quote_requests row + the customer profile
      // and pre-fills each field only if still empty, so the vendor's input
      // is never overwritten by a late-arriving prefill (CF29).
      scheduled_date: "",
      scheduled_time: "",
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      service_address: "",
    },
  });

  // Load customer's service addresses AND profile info for pre-fill.
  // The profile holds full_name + phone + email captured when the customer
  // requested the quote; we pre-fill the drawer so the vendor does not have
  // to retype the contact details.
  // - Never overwrite a field the vendor has already started typing (CF29).
  // - Tolerate missing data: partial profiles must not crash the dialog (CF30).
  useEffect(() => {
    (async () => {
      // Helper: only set if the field is currently empty so vendor input wins
      const fillIfEmpty = (field: keyof QuoteToBookingForm, value: string) => {
        if (!value) return;
        const current = String(form.getValues(field) || '').trim();
        if (current) return;
        form.setValue(field, value);
      };

      try {
        const [addrRes, profRes, qrRes] = await Promise.all([
          supabase
            .from('service_addresses')
            .select('*')
            .eq('profile_id', quoteRequest.customer_id)
            .order('is_default', { ascending: false }),
          (supabase as any)
            .from('profiles')
            .select('id, full_name, phone, email, user_id')
            .eq('id', quoteRequest.customer_id)
            .maybeSingle(),
          (supabase as any)
            .from('quote_requests')
            .select('square_meters, special_requests, scheduled_date, scheduled_time, customer_name, customer_phone, customer_email')
            .eq('id', quoteRequest.id)
            .maybeSingle(),
        ]);

        const addrs = addrRes?.data || [];
        if (addrRes?.error) {
          console.warn('quote -> booking: addresses lookup failed', addrRes.error.message);
        }
        setServiceAddresses(addrs);
        const defaultAddress = addrs.find((addr: any) => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          fillIfEmpty('service_address', `${defaultAddress.address_line1}, ${defaultAddress.city}`);
        }

        if (profRes?.error) {
          console.warn('quote -> booking: profile lookup failed', profRes.error.message);
        }
        const prof = profRes?.data as any;
        if (prof) {
          if (prof.full_name) fillIfEmpty('customer_name', String(prof.full_name).trim());
          if (prof.phone) fillIfEmpty('customer_phone', String(prof.phone));
          if (prof.email) fillIfEmpty('customer_email', String(prof.email));
        }

        // The quote_requests row itself stores what the customer typed when
        // submitting the quote. Use these as a higher-priority source than
        // the profile (still respecting "do not overwrite vendor input").
        const qr = qrRes?.data as any;
        if (qr) {
          if (qr.customer_name) fillIfEmpty('customer_name', String(qr.customer_name));
          if (qr.customer_phone) fillIfEmpty('customer_phone', String(qr.customer_phone));
          if (qr.customer_email) fillIfEmpty('customer_email', String(qr.customer_email));
          if (qr.scheduled_date) fillIfEmpty('scheduled_date', String(qr.scheduled_date));
          if (qr.scheduled_time) {
            fillIfEmpty('scheduled_time', String(qr.scheduled_time).slice(0, 5));
          }
          if (qr.special_requests) {
            (form as any).__quoteNotes = qr.special_requests;
          }
        }
      } catch (error) {
        // Never tear down the dialog on prefill failure (CF30); the vendor
        // can still fill the form manually.
        console.error('Error loading quote prefill data:', error);
      }
    })();
  }, [quoteRequest.customer_id, quoteRequest.id]);

  // Local state for creating a new service address if none exist
  const [addrLabel, setAddrLabel] = useState<string>("Home");
  const [addrLine1, setAddrLine1] = useState<string>("");
  const [addrLine2, setAddrLine2] = useState<string>("");
  const [addrCity, setAddrCity] = useState<string>("");
  const [addrPostal, setAddrPostal] = useState<string>("");
  const [addrCountry, setAddrCountry] = useState<string>("Italy");

  const handleCreateAddress = async () => {
    try {
      if (!addrLabel || !addrLine1 || !addrCity || !addrPostal) {
        toast({ title: "Missing fields", description: "Fill label, address, city and postal code.", variant: "destructive" });
        return;
      }

      // Insert directly relying on RLS policy "Companies can insert addresses for quote customers"
      const { data: inserted, error: insertError } = await (supabase as any)
        .from('service_addresses')
        .insert({
          profile_id: quoteRequest.customer_id,
          label: addrLabel,
          address_line1: addrLine1,
          address_line2: addrLine2 || null,
          city: addrCity,
          postal_code: addrPostal,
          country: addrCountry,
          is_default: true,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      const newId = inserted?.id as string;

      // Reload addresses and select the new one
      const { data: reloaded } = await (supabase as any)
        .from('service_addresses')
        .select('*')
        .eq('profile_id', quoteRequest.customer_id)
        .order('is_default', { ascending: false });

      setServiceAddresses(reloaded || []);
      setSelectedAddressId(newId);
      form.setValue('service_address', `${addrLine1}, ${addrCity}`);
      toast({ title: "Address added", description: "Service address created and selected." });
    } catch (e: any) {
      console.error(e);
      toast({ title: "Error", description: e.message || 'Failed to create address', variant: 'destructive' });
    }
  };

  const handleSubmit = async (data: QuoteToBookingForm) => {
    if (!selectedAddressId) {
      toast({
        title: "Error",
        description: "Please select a service address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Prefer RPC for atomic creation and immediate booking id
      const normalizedTime = data.scheduled_time.length === 5 ? `${data.scheduled_time}:00` : data.scheduled_time;
      const { data: rpcBookingId, error: rpcError } = await (supabase as any).rpc('create_booking_from_quote', {
        p_quote_request_id: quoteRequest.id,
        p_scheduled_date: data.scheduled_date,
        p_scheduled_time: normalizedTime,
        p_customer_name: data.customer_name,
        p_customer_phone: data.customer_phone,
        p_customer_email: data.customer_email,
        p_service_address_id: selectedAddressId,
      });

      if (!rpcError && rpcBookingId) {
        toast({ title: "Success", description: `Booking created successfully!` });
        onBookingCreated(rpcBookingId as string);
        onClose();
        return;
      }

      // Fallback: use trigger path if RPC is not available
      const { error: updateError } = await (supabase as any)
        .from('quote_requests')
        .update({
          status: 'accepted',
          scheduled_date: data.scheduled_date,
          scheduled_time: normalizedTime,
          customer_name: data.customer_name,
          customer_phone: data.customer_phone,
          customer_email: data.customer_email,
          service_address_id: selectedAddressId,
        })
        .eq('id', quoteRequest.id);

      if (updateError) throw updateError;

      // The trigger will automatically create the booking
      // Let's poll briefly for the created booking
      setTimeout(async () => {
        try {
          const { data: bookings, error: bookingError } = await supabase
            .from('bookings')
            .select('id, booking_number')
            .eq('customer_id', quoteRequest.customer_id)
            .eq('company_id', quoteRequest.company_id)
            .eq('service_id', quoteRequest.service_id)
            .eq('scheduled_date', data.scheduled_date)
            .eq('scheduled_time', normalizedTime)
            .order('created_at', { ascending: false })
            .limit(1);

          if (bookingError) throw bookingError;

          if (bookings && bookings.length > 0) {
            toast({
              title: "Success",
              description: `Booking created successfully! Booking number: ${bookings[0].booking_number}`,
            });
            onBookingCreated(bookings[0].id);
          } else {
            toast({
              title: "Warning",
              description: "Quote accepted but booking creation may be delayed. Please check your bookings.",
            });
          }
        } catch (error) {
          console.error('Error checking booking creation:', error);
        }
      }, 2000);
    } catch (error) {
      console.error('Error converting quote to booking:', error);
      toast({
        title: "Error",
        description: "Failed to convert quote to booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"
  ];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Convert Quote to Booking
          </DialogTitle>
          <DialogDescription>
            Schedule the service and create a booking for this accepted quote.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Quote Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Quote Price:</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(quoteRequest.quote_price)}
                </span>
              </div>
              {quoteRequest.special_requests && (
                <div>
                  <span className="font-medium">Special Requests:</span>
                  <p className="text-sm text-gray-600 mt-1">{quoteRequest.special_requests}</p>
                </div>
              )}
              {quoteRequest.photos && quoteRequest.photos.length > 0 && (
                <div>
                  <span className="font-medium">Photos:</span>
                  <p className="text-sm text-gray-600">{quoteRequest.photos.length} files attached</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date */}
                <FormField
                  control={form.control}
                  name="scheduled_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Service Date
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Time */}
                <FormField
                  control={form.control}
                  name="scheduled_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Service Time
                      </FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">Select time</option>
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Customer Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="customer_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter customer name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customer_phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Phone Number
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Enter phone number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="customer_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter email address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Service Address */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Service Address
                </h3>

                {serviceAddresses.length > 0 ? (
                  <div className="space-y-2">
                    <Label>Select Address</Label>
                    {serviceAddresses.map((address) => (
                      <div
                        key={address.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedAddressId === address.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedAddressId(address.id);
                          form.setValue('service_address', `${address.address_line1}, ${address.city}`);
                        }}
                      >
                        <div className="font-medium">{address.label}</div>
                        <div className="text-sm text-gray-600">
                          {address.address_line1}
                          {address.address_line2 && `, ${address.address_line2}`}
                          , {address.city} {address.postal_code}
                        </div>
                        {address.is_default && (
                          <Badge variant="secondary" className="mt-1">Default</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 border border-dashed border-gray-300 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="h-5 w-5 text-gray-500" />
                      <p className="text-gray-700">No service addresses found. Add one now:</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Label</Label>
                        <Input value={addrLabel} onChange={(e) => setAddrLabel(e.target.value)} placeholder="Home" />
                      </div>
                      <div>
                        <Label>Postal Code</Label>
                        <Input value={addrPostal} onChange={(e) => setAddrPostal(e.target.value)} placeholder="00100" />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Address Line 1</Label>
                        <Input value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} placeholder="Via Roma 10" />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Address Line 2 (optional)</Label>
                        <Input value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)} placeholder="Scala A, Interno 3" />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input value={addrCity} onChange={(e) => setAddrCity(e.target.value)} placeholder="Roma" />
                      </div>
                      <div>
                        <Label>Country</Label>
                        <Input value={addrCountry} onChange={(e) => setAddrCountry(e.target.value)} placeholder="Italy" />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button type="button" variant="secondary" onClick={handleCreateAddress}>
                        Add Address
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !selectedAddressId}
                  className="min-w-[120px]"
                >
                  {isSubmitting ? "Creating..." : "Create Booking"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
